/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: AgentsPage.tsx (Agents)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Agents list page with table view, search, pagination, and actions.

 🔧 Notes:
    - Refactored from monolithic file
    - Logic delegated to hooks/
    - API calls moved to api/
    - JSX rendering for columns kept here (must be in .tsx file)

 🔁 Last Updated: 2025-01-15 by Rodrigo Cuadra (Refactored)
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Edit, Trash2, Copy } from 'lucide-react';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { ModulePageContainer } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { Badge } from '../../components/ui/badge';
import { useAgentList } from './hooks';
import { deleteAgentById, getAgentDuplicateUrl } from './agentsActions';
import type { AgentResponse } from './types/agents.types';

export default function AgentsPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();

  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; agent: AgentResponse | null }>({
    isOpen: false,
    agent: null
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    data,
    loading,
    columns: baseColumns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useAgentList();

  // Add render functions to columns (JSX must be in .tsx file)
  const columns = useMemo(() => {
    return baseColumns.map((column) => {
      if (column.key === 'name') {
        return {
          ...column,
          render: (_value: unknown, item: AgentResponse) => (
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{item?.name || '-'}</span>
            </div>
          )
        };
      }

      if (column.key === 'extension') {
        return {
          ...column,
          render: (_value: unknown, item: AgentResponse) => (
            <span className="font-mono text-sm">{item?.extension || '-'}</span>
          )
        };
      }

      if (column.key === 'status') {
        return {
          ...column,
          render: (_value: unknown, item: AgentResponse) => (
            <Badge variant="outline" size="sm">
              {t(`agents.statuses.${item?.status?.replace(/[()]/g, '').replace(/\s/g, '')}`)}
            </Badge>
          )
        };
      }

      if (column.key === 'enabled') {
        return {
          ...column,
          render: (_value: unknown, item: AgentResponse) => (
            <Badge 
              variant={item?.enabled ? 'default' : 'secondary'}
              size="sm"
            >
              {item?.enabled ? t('common.enabled') : t('common.disabled')}
            </Badge>
          )
        };
      }

      if (column.key === 'createdAt') {
        return {
          ...column,
          render: (_value: unknown, item: AgentResponse) => (
            <span className="text-sm text-muted-foreground">
              {item?.createdAt ? new Date(item.createdAt).toLocaleDateString() : '-'}
            </span>
          )
        };
      }

      if (column.key === 'actions') {
        return {
          ...column,
          render: (_value: unknown, item: AgentResponse) => renderRowActions(item)
        };
      }

      return column;
    });
  }, [baseColumns, t]);

  const handleCreate = useCallback(() => {
    navigate('/pbx/call-center/agents/new');
  }, [navigate]);

  const handleEdit = useCallback((item: AgentResponse) => {
    navigate(`/pbx/call-center/agents/${item.uuid || item.id}`);
  }, [navigate]);

  const handleDuplicate = useCallback((item: AgentResponse) => {
    navigate(getAgentDuplicateUrl(item.uuid || item.id));
  }, [navigate]);

  const handleDeleteClick = useCallback((item: AgentResponse) => {
    setDeleteModal({ isOpen: true, agent: item });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.agent) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteAgentById(deleteModal.agent.id);

      showSuccess(t('agents.messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, agent: null });
      refresh();
    } catch (error) {
      const message = error instanceof Error ? error.message : t('agents.messages.deleteError');
      showError(message);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal.agent, refresh, showError, showSuccess, t]);

  const renderRowActions = useMemo(() => (item: AgentResponse) => (
    <div className="flex items-center gap-2">
      <SimpleTooltip content={t('common.edit')} position="top">
        <button
          onClick={() => handleEdit(item)}
          className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common.duplicate')} position="top">
        <button
          onClick={() => handleDuplicate(item)}
          className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common.delete')} position="top">
        <button
          onClick={() => handleDeleteClick(item)}
          className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
        </button>
      </SimpleTooltip>
    </div>
  ), [handleDeleteClick, handleDuplicate, handleEdit, t]);

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          data={data}
          columns={columns}
          loading={loading}
          pagination={pagination}
          searchQuery={searchQuery}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSearch={handleSearch}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onAdd={handleCreate}
          onDuplicate={handleDuplicate}
          onColumnToggle={handleColumnToggle}
          title={t('agents.title')}
          description={t('agents.description')}
          titleIcon={<User className="h-5 w-5 text-primary" />}
          addButtonText={t('agents.actions.add')}
          renderActions={renderRowActions}
          searchPlaceholder={t('agents.search.placeholder')}
          emptyMessage={t('agents.messages.noAgents')}
        />
      }
      formView={null}
    >
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, agent: null })}
        onConfirm={handleDeleteConfirm}
        title={t('agents.messages.deleteTitle')}
        message={t('agents.messages.deleteMessage', { name: deleteModal.agent?.name || '' })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </ModulePageContainer>
  );
}
